import React from "react";
import { App } from "antd5";
import isEqual from "lodash.isequal";
import { Flex } from "styles/utility-components";

import {
  convertNoticeFiltersToView,
  EMPTY_FILTERS,
  generateNoticeAnalysisUrl,
} from "components/notices/utils";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { MARKET_ANALYSIS, useVariableValue } from "lib/featureFlags";
import { SavedViewType } from "lib/generated/app-api";
import { useGetLeadSubscriptionContents } from "lib/hooks/api/lead_subscriptions/useGetLeadSubscriptionContents";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { EventNames, useTracking } from "lib/tracking";
import { assert } from "lib/utils";
import { useUnpinView } from "../../../lib/hooks/api/notices/views/useUnpinView";
import { useUpdateView } from "../../../lib/hooks/api/notices/views/useUpdateView";
import { getAlert } from "../alerts";
import { createEditAlertsModalOpened } from "../tracking";
import { useMyFeedPageState } from "../useMyFeedPageState";
import { CreateViewModal } from "./CreateViewModal";
import { DuplicateViewModal } from "./DuplicateViewModal";
import { EditAlertsModal } from "./EditAlertsModal";
import { EditViewModal } from "./EditViewModal";

type ViewOptionsProps = {
  selectedView?: SavedViewType;
};

function ViewOptions({ selectedView }: ViewOptionsProps) {
  const { logEvent } = useTracking();
  const { message } = App.useApp();
  const { openPaywallModal } = usePaywall();
  const { filters, hasUserModifiedView, tableColumns, setSelectedView } = useMyFeedPageState();
  const { data: leadSubscription } = useGetLeadSubscriptionContents();
  const { mutate: unpinViewMutation } = useUnpinView();

  const subscription = useSubscription();
  const hasMarketAnalysisAccess = subscription.hasDataTypes("MARKET_ANALYSIS");
  const isMarketAnalysisEnabled = useVariableValue(MARKET_ANALYSIS, false);

  const [isEditAlertsModalOpen, setIsEditAlertsModalOpen] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = React.useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  const hasAlertsEnabled =
    selectedView &&
    leadSubscription &&
    Boolean(selectedView.id && getAlert(leadSubscription.personalSubscriptions, selectedView.id));

  const { mutate: mutateView } = useUpdateView("Update view dropdown", {
    onSuccess: (_data, vars) => {
      message.success(`Successfully updated ${vars.view.name}`);
      setSelectedView(vars.view.id, vars.view.name, "Update view");
    },
    onError: (_data, vars) => message.error(`Failed to update ${vars.view.name}`),
  });

  const handleMarketAnalysisClick = (evt: React.MouseEvent, id: string | undefined) => {
    if (!hasMarketAnalysisAccess) {
      evt.preventDefault();
      openPaywallModal("STRATEGY", {
        requiredDataSubscription: "MARKET_ANALYSIS",
        strategyPaywall: true,
      });
    } else {
      logEvent(EventNames.marketAnalysisCreated, {
        viewId: id ?? "N/A",
      });
    }
  };

  return (
    <Flex gap="8px" justifyContent="right">
      {selectedView && (
        <>
          <EditAlertsModal
            isOpen={isEditAlertsModalOpen}
            onClose={() => setIsEditAlertsModalOpen(false)}
            selectedView={selectedView}
          />
          <EditViewModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            selectedView={selectedView}
          />
          <DuplicateViewModal
            isOpen={isDuplicateModalOpen}
            onClose={() => setIsDuplicateModalOpen(false)}
            selectedView={selectedView}
            onSuccess={(id) => setSelectedView(id, "", "Duplicate view modal")}
          />
          <DropdownMenu
            trigger={<DropdownMenuTrigger buttonType="secondary">More</DropdownMenuTrigger>}
            width="fit-content"
            modal={false}
          >
            <DropdownMenuItem
              key="set-alerts"
              icon="bell01"
              iconProps={{
                fill: hasAlertsEnabled ? "sysTextDefault" : "transparent",
              }}
              onClick={() => {
                logEvent(
                  EventNames.alertModalOpened,
                  createEditAlertsModalOpened(selectedView.name, selectedView.id),
                );
                setIsEditAlertsModalOpen(true);
              }}
            >
              Set alerts
            </DropdownMenuItem>
            <DropdownMenuItem
              key="unpin"
              icon="pin02"
              onClick={() => {
                assert(selectedView.id);
                unpinViewMutation({ id: selectedView.id, name: selectedView.name });
              }}
            >
              Unpin view
            </DropdownMenuItem>
            <DropdownMenuItem
              key="duplicate"
              icon="copy07"
              onClick={() => {
                setIsDuplicateModalOpen(true);
              }}
            >
              Duplicate view
            </DropdownMenuItem>
            <DropdownMenuItem
              key="edit"
              icon="edit05"
              onClick={() => {
                setIsEditModalOpen(true);
              }}
            >
              Edit view
            </DropdownMenuItem>
          </DropdownMenu>
        </>
      )}
      {isMarketAnalysisEnabled && (
        <Button
          as="a"
          href={generateNoticeAnalysisUrl(filters, selectedView?.id, hasUserModifiedView)}
          icon="barChart12"
          buttonType="secondary"
          onClick={(e) => handleMarketAnalysisClick(e, selectedView?.id)}
        >
          Analyse
          {!hasMarketAnalysisAccess && <UIcon icon={"lock01"} size={14} />}
        </Button>
      )}
      <CreateViewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        selectedView={selectedView}
      />
      <DropdownMenu
        trigger={<DropdownMenuTrigger icon="save01">Save search</DropdownMenuTrigger>}
        width="fit-content"
        modal={false}
      >
        <DropdownMenuItem
          key="update-current-view"
          disabled={!selectedView || selectedView.isStandardView || !hasUserModifiedView}
          onClick={() => {
            assert(selectedView);
            const convertedViewToFilter = convertNoticeFiltersToView(filters, tableColumns);
            mutateView({
              view: {
                ...selectedView,
                view: convertedViewToFilter,
              },
            });
          }}
        >
          Update current view
        </DropdownMenuItem>
        <DropdownMenuItem
          key="save-as-new-view"
          disabled={!hasUserModifiedView || isEqual(filters, EMPTY_FILTERS)}
          onClick={() => {
            setIsCreateModalOpen(true);
          }}
        >
          Save as new view
        </DropdownMenuItem>
      </DropdownMenu>
    </Flex>
  );
}

export default ViewOptions;
